<template>
  <ion-app>
    <ion-router-outlet />
  </ion-app>
</template>

<script setup lang="ts">
import {IonApp, IonRouterOutlet, isPlatform, useBackButton, useIonRouter} from '@ionic/vue';
import {onMounted, watch} from "vue";
import {useRoute} from "vue-router";
import {ActionPerformed, PushNotifications, PushNotificationSchema, Token} from "@capacitor/push-notifications";
import {useUserStore} from "@/stores/user";
import useNotificationCentre from "@/composables/useNotificationCentre";
import {App, AppState, StateChangeListener} from '@capacitor/app';

import {APIActionPerformed, APINotification} from "@/constants/pushNotificationTypes";
import {Keyboard} from "@capacitor/keyboard";
import {startAndroidKeyboardAssist} from "@/utils/keyboardAssist";
import {Capacitor, SystemBars, SystemBarsStyle} from "@capacitor/core";
import useLocalStorage from "@/composables/useLocalStorage";
import {useAppearanceStore} from "@/stores/appearance";
import {NativeAppearance} from "@/utils/nativeAppearance";
import {storeToRefs} from "pinia";

const userStore = useUserStore();
const route = useRoute();

const ionRouter = useIonRouter();
useBackButton(-1, () => {
  if (!ionRouter.canGoBack()) {
    App.exitApp();
  }
});

const setPushNotifications = async () => {
  const notificationCentre = await useNotificationCentre();

  // Bind listeners BEFORE register() so registrationError can catch APNS/FCM failures.
  await PushNotifications.addListener('registration', (token: Token) => {
    userStore.savePushToken(token.value);
  });

  await PushNotifications.addListener('registrationError', (error: any) => {
    console.warn('Push registration error', error);
  });

  await PushNotifications.addListener('pushNotificationReceived', (notification: APINotification) => {
    notificationCentre.handleNotification(notification, true);
  });

  await PushNotifications.addListener('pushNotificationActionPerformed', (notification: APIActionPerformed) => {
    notificationCentre.handleNotification(notification.notification, false);
  });

  try {
    const result = await PushNotifications.requestPermissions();
    if (result.receive === 'granted') {
      // Register with Apple / Google to receive push via APNS/FCM.
      // This can reject on iOS when capacitorDidRegisterForRemoteNotifications never fires
      // (simulator, missing entitlement, transient APNS issues) — non-fatal for the app.
      await PushNotifications.register();
    } else {
      userStore.setNotificationsBlocked(true);
    }
  } catch (e) {
    console.warn('Push notifications setup failed', e);
  }

  await App.addListener('appStateChange', async(state: AppState) => {
    const storage = await useLocalStorage();
    const accountToken = await storage.get('accountToken');
    if (state.isActive && accountToken) {
      await userStore.fetchUser();
    }
  });
}

onMounted(() => {
  if (Capacitor.isNativePlatform()) {
    setPushNotifications();
  }
  if (Capacitor.isNativePlatform() && isPlatform('ios')) {
    Keyboard.setAccessoryBarVisible({isVisible: true});
  }
  // Ionic ships scroll assist for iOS only and leaves Android to the native
  // webview resize, which is not reliable on every device. See keyboardAssist.
  if (Capacitor.getPlatform() === 'android') {
    startAndroidKeyboardAssist();
  }
});

// Android 16 (API 36) enforces edge-to-edge, so the webview draws behind the system
// bars and StatusBar.overlaysWebView no longer applies. The bar content then has to
// match whatever is painted underneath it, and that is not the same thing everywhere:
//
// - Below Android 15 the webview is inset instead, and the bars are a native strip
//   coloured by systemBarBackground in the Android theme, which follows the OS scheme
//   even when the user keeps the app light or dark (see stores/appearance).
// - From Android 15 the current view paints behind the bars, so what matters is the
//   app's own scheme plus views flagged with meta.darkSystemBars, which are dark
//   whichever scheme is active (e.g. the login video).
//
// Capacitor only injects a non-zero top safe area inset in the second case, so that is
// what tells the two apart. iOS picks the bar content from its interface style, which is
// set to the app's scheme below.
const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
const {colorScheme, isDark} = storeToRefs(useAppearanceStore());

const viewPaintsBehindStatusBar = () => {
  const inset = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-top');
  return parseFloat(inset) > 0;
};

let appliedStyle: SystemBarsStyle | null = null;

const syncSystemBars = () => {
  if (Capacitor.getPlatform() !== 'android') {
    return;
  }
  const onDarkBackground = viewPaintsBehindStatusBar()
    ? isDark.value || route.meta.darkSystemBars === true
    : prefersDark.matches;
  const style = onDarkBackground ? SystemBarsStyle.Dark : SystemBarsStyle.Light;
  if (style === appliedStyle) {
    return;
  }
  appliedStyle = style;
  SystemBars.setStyle({style});
};

watch([() => route.meta.darkSystemBars === true, isDark], syncSystemBars, {immediate: true});
prefersDark.addEventListener('change', syncSystemBars);

// What iOS draws itself (status bar, keyboard, the fade under the status bar, alerts)
// follows its interface style, the phone's setting unless overridden, so hand it the
// user's choice. See AppearancePlugin.swift.
if (Capacitor.getPlatform() === 'ios') {
  watch(colorScheme, (style) => {
    NativeAppearance.setStyle({style});
  }, {immediate: true});
}

// The insets are injected as inline custom properties on <html> once the webview has
// been laid out, which can land after this file first runs, so pick them up then too.
new MutationObserver(syncSystemBars).observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['style'],
});

</script>
